import { useEffect, useState, memo, useCallback, useMemo } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const FeedbackAdmin = memo(() => {
  const { t, i18n } = useTranslation();
  const [feedback, setFeedback] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");


  const isArabic = i18n.language?.startsWith('ar');

  const headers = useMemo(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchFeedback = useCallback(async (p = 1) => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/admin/feedback?page=${p}&q=${encodeURIComponent(q)}`, { headers });
      setFeedback(res.data.data || []);
      setTotal(res.data.total || res.data.meta?.total || 0);
      setPage(p);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        alert("Invalid Admin Secret for Feedback. Access denied.");
      } else {
        alert("Failed to fetch feedback");
      }
    }
  }, [q, headers]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);
  useEffect(() => { fetchFeedback(1); }, [q, fetchFeedback]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const del = async () => {
    try {
      await axios.post(
        `${CONFIG.API_URL}/api/admin/feedback/delete`,
        { ids: Array.from(selected) },
        { headers }
      );
      setSelected(new Set());
      fetchFeedback();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        alert("Access denied. Invalid Admin Secret for Feedback.");
      } else {
        alert("Delete failed");
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
      <div className="flex justify-between items-center mb-12">
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.feedback_page.title')}</h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
          >
            <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
            {t('admin.common.back', 'Back')}
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-col md:flex-row gap-4 items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">
        <div className="relative flex-1 w-full">
          <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('admin.common.search_placeholder', 'Search...')} className="w-full pl-12 pr-4 py-3 rounded-2xl border-none bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium" />
        </div>

        <div className="flex items-center gap-2">
          <button onClick={del} className="bg-red-100 text-red-700 hover:bg-red-600 hover:text-white px-6 py-3 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2 shadow-sm">
            <i className="fas fa-trash"></i> {t('admin.feedback_page.delete_selected')}
          </button>

        </div>
      </div>

      <div className="space-y-4">
        {feedback.map(f => (
          <div key={f.id} className={`group border border-gray-100 p-6 rounded-[24px] hover:shadow-lg hover:border-brand-primary/20 transition-all duration-300 relative ${selected.has(f.id) ? "bg-brand-primary/5 ring-2 ring-brand-primary/20" : "bg-white"}`}>
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-gray-100 text-gray-500">
                #{f.id}
              </span>
            </div>
            <div className="flex justify-between items-start mb-4">
              <div className="pt-1">
                <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} className="w-5 h-5 accent-brand-primary rounded-lg cursor-pointer" />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-bold text-gray-900 text-lg">
                    {isArabic
                      ? (f.name === 'عميل مجهول' ? t('admin.feedback_page.anonymous', 'مجهول') : f.name)
                      : (f.name === 'Anonymous Customer' ? t('admin.feedback_page.anonymous', 'Anonymous') : f.name)
                    }
                    {/* {f.name === 'Anonymous Customer' ? t('admin.feedback_page.anonymous', 'Anonymous') : f.name} */}
                  </h3>
                  {f.email && <span className="text-[10px] font-bold text-brand-primary bg-brand-primary/10 px-3 py-1 rounded-full uppercase tracking-wider">{f.email}</span>}
                  {f.rating && (
                    <div className="flex items-center text-amber-400 text-sm gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`fas fa-star ${i < f.rating ? "" : "text-gray-300"}`}></i>
                      ))}
                      <span className="text-amber-700 font-bold ml-1">{f.rating}</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-600 leading-relaxed mb-4 text-sm md:text-base bg-gray-50 p-4 rounded-2xl border border-gray-100">{f.message}</p>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{new Date(f.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-4 items-center mt-12">
          <button onClick={() => fetchFeedback(Math.max(1, page - 1))} disabled={page <= 1} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "→" : "←"}</button>
          <div className="text-xs font-black uppercase tracking-widest text-gray-400">{t('admin.common.page', { current: page, total: Math.ceil(total / 20) })}</div>
          <button onClick={() => fetchFeedback(page + 1)} disabled={page >= Math.ceil(total / 20)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "←" : "→"}</button>
        </div>
      )}
    </div>
  );
});

export default FeedbackAdmin;
